// eslint-disable-next-line import/no-unresolved
import { MenuItem } from "primereact/menuitem";

export interface BreadcrumbEntry {
  /** Display label, e.g. "API Security", "Catalog" */
  label: string;
  /** Route to navigate to when clicked, e.g. "/path/abc123" */
  route: string;
}

export type BreadcrumbDisplay = "navbar" | "page";

/** @deprecated Use BreadcrumbRoot instead. Kept for Layout backward compat. */
export type AppRoot = "learn" | "admin";

/** The root/home item for the breadcrumb trail (leftmost crumb). */
export interface BreadcrumbRoot {
  label: string;
  route: string;
}

/**
 * Map of route → root definition. When a page's route matches a key,
 * the trail resets and the root (home item) is set to the corresponding value.
 * The page itself is NOT added to the trail (it IS the root).
 */
export const BREADCRUMB_RESET_POINTS: Record<string, BreadcrumbRoot> = {
  "/": { label: "My Journey", route: "/" },
  "/catalog": { label: "Catalog", route: "/catalog" },
  "/assess": { label: "Assessments", route: "/assess" },
  "/admin": { label: "Admin", route: "/admin" },
  "/profile": { label: "Profile", route: "/profile" },
};

/** Persisted state shape in sessionStorage. */
export interface BreadcrumbState {
  /** The active root (home item). Set by whichever reset point was last visited. */
  root: BreadcrumbRoot;
  /** Auto-accumulated breadcrumb trail (excluding the root). Last entry = current page. */
  trail: BreadcrumbEntry[];
  /** Where breadcrumbs are currently being rendered. */
  display: BreadcrumbDisplay;
}

/** Config for the useBreadcrumb hook. Each page sends only its own entry. */
export interface UseBreadcrumbConfig {
  /** This page's breadcrumb label. */
  label: string;
  /** This page's route. */
  route: string;
  /** Where to render: "navbar" replaces the nav menu, "page" renders in-page. Default: "navbar". */
  display?: BreadcrumbDisplay;
  /**
   * When true, clears the existing trail before adding this entry.
   * Use on top-level section pages (e.g. sidebar list pages) so navigating
   * between sections always produces a clean `Root > Section` breadcrumb.
   */
  resetTrail?: boolean;
}

export interface BreadcrumbContextType {
  /** Current breadcrumbs as PrimeReact MenuItems. undefined = no breadcrumbs (show normal navbar). */
  breadcrumbs: MenuItem[] | undefined;
  /** Context-aware home item for PrimeReact BreadCrumb component. */
  homeItem: MenuItem;
  /** Where the current breadcrumbs should be rendered: "navbar" or "page". */
  display: BreadcrumbDisplay;

  /** Add this page's entry to the trail. Handles reset detection, truncation, and append. */
  updateTrail: (entry: BreadcrumbEntry, display?: BreadcrumbDisplay, resetTrail?: boolean) => void;

  /**
   * Legacy API: Set breadcrumbs directly as PrimeReact MenuItems.
   * For backward compatibility with existing useNavContext consumers.
   * Pass undefined to clear.
   */
  setBreadcrumbs: (breadcrumbs: MenuItem[] | undefined) => void;

  /**
   * @internal Hide breadcrumbs from display without clearing sessionStorage.
   * Used by useBreadcrumb hook on unmount.
   */
  _hideDisplay: () => void;
}
